/**
 * This component allows users to select a color for paint products. It handles
 * displaying color palettes, searching for colors, and managing the selected color state.
 */
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { useColorDrawerState } from '@/data/state/useColorDrawerState';
import { Alert, Box, Card, Divider, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo } from 'react';

import { BuyByColorDrawer } from '@/components/blocks/ProductDetails/parts/buyByColor/BuyByColorDrawer';
import { ColorSelected } from '@/components/blocks/ProductDetails/parts/buyByColor/ColorSelected';
import { ColorsPaletteDefault } from '@/components/blocks/ProductDetails/parts/buyByColor/ColorsPaletteDefault';
import { SearchColor } from '@/components/blocks/ProductDetails/parts/buyByColor/SearchColor';
import {
	buyByColorAlertSx,
	buyByColorBoxColorSelectedSx,
	buyByColorCardSx,
	buyByColorCardTitleSx,
} from '@/components/blocks/ProductDetails/styles/buyByColor';
import {
	getColorCode,
	getColorCodeURL,
	removeColorCode,
	saveColorCodeURL,
} from '@/components/blocks/ProductDetails/utils/utils';
import {
	BASE_COLOR_IDENTIFIER_SUFFIX,
	NO_RANGE_INDICATOR,
	NOT_APPLICABLE,
	WHITE_BASE_COLOR_NAME,
} from '@/components/content/ProductDetails/constants';
import {
	NAVIGABLE_SECTION_IDS,
	useNavigableSection,
} from '@/components/content/ProductDetails/hooks/useNavigableSection';
import { usePaintColors } from '@/data/Content/usePaintColors';
import { LineRangeColor, PaintColor, ProductBaseColor } from '@/data/types/ColorType';
import { Selection } from '@/data/types/Product';
import {
	OpenSansFontWeights,
	PASTELES_FAMILY_COLOR,
} from 'shared/custom/constants/commonConstants';

interface BuyByColorProps {
	product: ReturnType<typeof useProductDetails>['product'];
	baseColorInfo: ProductBaseColor;
	brand: string;
	partNumber: string;
	selection?: Selection;
	onAttributeChange: (identifier: string, value: string) => void;
}

export const BuyByColor: FC<BuyByColorProps> = (props) => {
	const navigableSectionProps = useNavigableSection({
		sectionId: NAVIGABLE_SECTION_IDS.PRODUCT_BUY_BY_COLOR,
	});

	const { product, baseColorInfo, partNumber, selection, onAttributeChange } = props;
	const { brandColor, baseColor, lineColor } = baseColorInfo;
	const HDMProductDetail = useLocalization('HDMProductDetail');
	const transparentWarningMessage = HDMProductDetail.BuyByColor.labels.buyByColorWarningMessage.t();
	const [before, after] = transparentWarningMessage.split('transparente');
	const { colorDrawerState, actions: colorDrawerActions } = useColorDrawerState();
	const { colorCodeSelected, range: colorRangeSelected } = colorDrawerState;

	const baseSelectedKey = Object.keys(selection?.attrsByIdentifier ?? {}).find((k) =>
		k.startsWith(BASE_COLOR_IDENTIFIER_SUFFIX)
	);
	const baseSelectedValue =
		baseSelectedKey !== undefined ? selection?.attrsByIdentifier?.[baseSelectedKey] : undefined;

	const { lineRangeColors } = usePaintColors();
	const lineRangeColorsFiltered = useMemo(
		() =>
			lineRangeColors?.filter(
				(lineRange: LineRangeColor) =>
					lineRange?.brand === brandColor && lineRange?.line === lineColor
			),
		[brandColor, lineColor, lineRangeColors]
	);

	const isTransparentBase =
		baseSelectedValue !== WHITE_BASE_COLOR_NAME && colorCodeSelected?.code === NOT_APPLICABLE;

	// The default swatch of every base lives in the "#N/D" range, one entry per base.
	// When the product's base is missing there, no color can be resolved: the base is
	// never rendered and the "Seleccionar color" CTA never mounts, so the whole
	// section is hidden instead of showing a broken picker.
	const hasBaseInPaintData = useMemo(
		() =>
			Boolean(
				lineRangeColorsFiltered
					?.find((lineRange: LineRangeColor) => lineRange?.range === NO_RANGE_INDICATOR)
					?.colors.some((color: PaintColor) => color?.base === baseColor)
			),
		[baseColor, lineRangeColorsFiltered]
	);

	const initializeColorData = useCallback(() => {
		let colorBase: LineRangeColor | undefined;
		let baseRangeColorDefault: PaintColor | undefined;
		let colorCode;
		let range: string | null = null;
		let URLColorCode: string | undefined;
		const colorCodeLocalStorage = getColorCode();

		if (colorCodeLocalStorage) {
			URLColorCode = colorCodeLocalStorage;
			removeColorCode();
			saveColorCodeURL(colorCodeLocalStorage);
		} else {
			URLColorCode = getColorCodeURL();
		}

		if (!URLColorCode) {
			colorBase = lineRangeColors?.find(
				(lineRange: LineRangeColor) =>
					lineRange?.brand === brandColor &&
					lineRange?.line === lineColor &&
					lineRange.range === NO_RANGE_INDICATOR
			);

			baseRangeColorDefault = colorBase?.colors.find(
				(color: PaintColor) => color?.base === baseColor
			);

			colorCode = baseRangeColorDefault?.code;

			range = baseColor === WHITE_BASE_COLOR_NAME ? PASTELES_FAMILY_COLOR : null;
		} else {
			const colorBases = lineRangeColors?.filter(
				(lineRange: LineRangeColor) =>
					lineRange?.brand === brandColor && lineRange?.line === lineColor
			);

			// Search real ranges first; "#N/D" is a placeholder range (a base's own
			// default swatch) and should only match when no real range has the color.
			const orderedColorBases = colorBases
				? [...colorBases].sort((a, b) =>
						a.range === NO_RANGE_INDICATOR ? 1 : b.range === NO_RANGE_INDICATOR ? -1 : 0
				  )
				: undefined;

			if (orderedColorBases) {
				for (const base of orderedColorBases) {
					const colorSelect = base?.colors.find(
						(color: PaintColor) => color?.code === URLColorCode
					);
					if (colorSelect) {
						range = base?.range;
						baseRangeColorDefault = colorSelect;
						colorBase = base;
						colorCode = colorSelect?.code;
						break;
					}
				}
			}
			colorCode = baseRangeColorDefault?.code;
		}

		const selectedRangeColors = lineRangeColorsFiltered?.filter(
			(lineColor) => lineColor.range === range
		)[0];

		// Update global state using the custom hook's actions
		colorDrawerActions.setColorDrawerData({
			lineColorValue: lineColor,
			base: baseColor,
			colorCodeSelected: baseRangeColorDefault,
			colorCode,
			partNumber,
			range,
			superSKUAttributes: product?.definingAttributes,
			colorRangeSelected: range,
			rangeColors: selectedRangeColors?.colors,
			pendingRange: range,
			colorSelectedDrawer: URLColorCode ? baseRangeColorDefault : undefined,
			hasConfirmedColor: Boolean(URLColorCode),
		});
	}, [
		baseColor,
		brandColor,
		colorDrawerActions,
		lineColor,
		lineRangeColors,
		partNumber,
		product?.definingAttributes,
		lineRangeColorsFiltered,
	]);

	const initializeWhiteBaseRange = () => {
		if (!colorRangeSelected && baseColorInfo?.hasBaseColor && baseColor === WHITE_BASE_COLOR_NAME) {
			return PASTELES_FAMILY_COLOR;
		}
		return colorRangeSelected;
	};

	useEffect(() => {
		if (lineRangeColors) {
			initializeColorData();
		}
	}, [initializeColorData, lineRangeColors]);

	if (lineRangeColors && !hasBaseInPaintData) return null;

	return (
		<>
			<Card sx={buyByColorCardSx} id="buyByColorCard" {...navigableSectionProps}>
				<Typography variant={'h5'} sx={buyByColorCardTitleSx}>
					{HDMProductDetail.BuyByColor.labels.buyByColorCardTitle.t()}
				</Typography>
				<SearchColor onAttributeChange={onAttributeChange} brand={brandColor} line={lineColor} />
				{isTransparentBase ? (
					<Alert severity="warning" sx={buyByColorAlertSx}>
						<Typography component="span">
							{before}
							<Typography component="span" fontWeight={OpenSansFontWeights.bold}>
								{HDMProductDetail.BuyByColor.labels.transparent.t()}
							</Typography>
							{after}
						</Typography>
					</Alert>
				) : null}
				<Box sx={buyByColorBoxColorSelectedSx}>
					{colorCodeSelected ? (
						<ColorSelected isDrawer={false} allLineColors={lineRangeColorsFiltered} />
					) : null}
					<Divider />
					<ColorsPaletteDefault
						colorRangeSelected={initializeWhiteBaseRange() ?? ''}
						isDrawer={false}
						allLineColors={lineRangeColorsFiltered}
					/>
				</Box>
			</Card>
			<BuyByColorDrawer
				allLineColors={lineRangeColorsFiltered}
				superSkuAttributes={product?.definingAttributes}
				onAttributeChange={onAttributeChange}
			/>
		</>
	);
};

export default BuyByColor;
